import { useEffect, useRef, useState, useMemo } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { X, Plus, Minus, LocateFixed, Home as HomeIcon } from "lucide-react";
import { resolveCategoryStyle } from "./placeCategories";

// ── Testi fissi IT/EN/ES/FR (inglese come riserva) ──
const CM_T = {
  mappa: { it: "Mappa", en: "Map", es: "Mapa", fr: "Carte", de: "Karte" },
  posti: { it: "posti", en: "places", es: "sitios", fr: "lieux", de: "Orte" },
  sconti: { it: "sconti", en: "deals", es: "ofertas", fr: "offres", de: "Rabatte" },
  tutti: { it: "Tutti", en: "All", es: "Todos", fr: "Tous", de: "Alle" },
  centra: { it: "Centra sulla mia posizione", en: "Center on my location", es: "Centrar en mi ubicación", fr: "Centrer sur ma position", de: "Auf meinen Standort zentrieren" },
  gpsSpiega: {
    it: "Usiamo la vostra posizione solo per mostrarvi dove siete sulla mappa. Non la salviamo.",
    en: "We use your location only to show where you are on the map. We don't store it.",
    es: "Usamos tu ubicación solo para mostrarte dónde estás en el mapa. No la guardamos.",
    fr: "Nous utilisons votre position uniquement pour vous situer sur la carte. Nous ne la conservons pas.",
    de: "Wir nutzen deinen Standort nur, um dich auf der Karte zu zeigen. Wir speichern ihn nicht.",
  },
  gpsNo: {
    it: "Posizione non disponibile, vi mostriamo la casa.",
    en: "Location unavailable, showing the property.",
    es: "Ubicación no disponible, mostramos el alojamiento.",
    fr: "Position indisponible, nous affichons le logement.",
    de: "Standort nicht verfügbar, wir zeigen die Unterkunft.",
  },
  chiudi: { it: "Chiudi", en: "Close", es: "Cerrar", fr: "Fermer", de: "Schließen" },
};
const cmt = (k, lang) => (CM_T[k] && (CM_T[k][lang] || CM_T[k].it)) || k;

function distanceKm(a, b) {
  const R = 6371, rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function placeDivIcon(style, recommended, selected, label) {
  const iconSvg = renderToStaticMarkup(<style.Icon size={16} color="#fff" strokeWidth={2} />);
  const scale = selected ? 1.25 : 1;
  const star = recommended
    ? `<span style="position:absolute;top:-3px;right:-4px;width:17px;height:17px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.35)"><svg width="12" height="12" viewBox="0 0 24 24" fill="#FFC700" stroke="#FFC700"><polygon points="12 2 15 9 22 9.3 16.5 14 18.5 21 12 17 5.5 21 7.5 14 2 9.3 9 9"/></svg></span>`
    : "";
  const html = `<div role="button" aria-label="${(label || "").replace(/"/g, "'")}" style="position:relative;width:34px;height:44px;transform:scale(${scale});transform-origin:bottom center;transition:transform .18s ease">
    <svg width="34" height="44" viewBox="0 0 34 44" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 4px 6px rgba(0,0,0,.35))">
      <path d="M17 1.5C8.9 1.5 2.5 7.9 2.5 16c0 9.8 14.5 26.5 14.5 26.5S31.5 25.8 31.5 16C31.5 7.9 25.1 1.5 17 1.5Z" fill="${style.color}" stroke="#fff" stroke-width="2.5"/>
    </svg>
    <span style="position:absolute;top:7px;left:0;right:0;height:18px;display:flex;align-items:center;justify-content:center">${iconSvg}</span>
    ${star}
  </div>`;
  return L.divIcon({ className: "", html, iconSize: [34, 44], iconAnchor: [17, 44] });
}

function homeDivIcon(color) {
  const iconSvg = renderToStaticMarkup(<HomeIcon size={18} color={color || "#F6C945"} strokeWidth={2.2} />);
  const html = `<div style="width:34px;height:34px;border-radius:11px;background:#16130F;border:2.5px solid #fff;box-shadow:0 5px 14px -4px rgba(0,0,0,.6);display:flex;align-items:center;justify-content:center">${iconSvg}</div>`;
  return L.divIcon({ className: "", html, iconSize: [34, 34], iconAnchor: [17, 17] });
}

function discountDivIcon(selected) {
  const scale = selected ? 1.22 : 1;
  const html = `<div role="button" style="width:32px;height:32px;transform:scale(${scale});transform-origin:center;border-radius:50%;background:#E5372B;border:2.5px solid #fff;box-shadow:0 4px 11px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:900;font-family:Archivo,system-ui,sans-serif;font-size:16px;transition:transform .18s ease">%</div>`;
  return L.divIcon({ className: "", html, iconSize: [32, 32], iconAnchor: [16, 16] });
}

function userDivIcon() {
  const html = `<div style="position:relative;width:26px;height:26px">
    <span class="wb3-gps-ring" style="position:absolute;inset:0;border-radius:50%;background:rgba(47,123,246,.28)"></span>
    <span style="position:absolute;top:6px;left:6px;width:14px;height:14px;border-radius:50%;background:#2F7BF6;border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.45)"></span>
  </div>`;
  return L.divIcon({ className: "", html, iconSize: [26, 26], iconAnchor: [13, 13] });
}

function clusterIcon(cluster) {
  const n = cluster.getChildCount();
  const size = n < 10 ? 38 : n < 50 ? 44 : 52;
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#16130F;border:2.5px solid #fff;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-family:Archivo,system-ui,sans-serif;font-size:${n < 100 ? 15 : 13}px;box-shadow:0 6px 16px -6px rgba(0,0,0,.6)">${n}</div>`,
    iconSize: [size, size],
  });
}

function MapController({ onReady }) {
  const map = useMap();
  useEffect(() => {
    onReady(map);
    const id = setTimeout(() => { try { map.invalidateSize(); } catch (_) {} }, 160);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function MarkersLayer({ places, discounts, home, homeColor, onSelect, onSelectDiscount, selectedId, catLabel }) {
  const map = useMap();
  useEffect(() => {
    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false, maxClusterRadius: 46, spiderfyOnMaxZoom: true,
      iconCreateFunction: clusterIcon,
    });
    places.forEach((p) => {
      const style = resolveCategoryStyle(p.category);
      const label = `${p.name || ""}, ${catLabel ? catLabel(p.category) : (p.category || "")}`;
      const m = L.marker([p.lat, p.lng], {
        icon: placeDivIcon(style, p.recommended, p.id === selectedId, label),
        zIndexOffset: (p.recommended ? 1000 : 0) + (p.id === selectedId ? 500 : 0),
        title: label, alt: label, keyboard: true,
      });
      m.on("click", () => onSelect && onSelect(p));
      cluster.addLayer(m);
    });
    map.addLayer(cluster);
    // Pin sconti (pallini rossi con %) — layer separato, sempre in evidenza
    const discLayer = L.layerGroup();
    (discounts || []).forEach((d) => {
      if (!isFinite(d.lat) || !isFinite(d.lng)) return;
      const label = `${d.name || "Sconto"} — ${d.promo_desc || "sconto"}`;
      const m = L.marker([d.lat, d.lng], {
        icon: discountDivIcon(d.promo_id === selectedId),
        zIndexOffset: 2500 + (d.promo_id === selectedId ? 500 : 0),
        title: label, alt: label, keyboard: true,
      });
      m.on("click", () => onSelectDiscount && onSelectDiscount(d));
      discLayer.addLayer(m);
    });
    map.addLayer(discLayer);
    const homeMk = home ? L.marker([home.lat, home.lng], { icon: homeDivIcon(homeColor), zIndexOffset: 2000, interactive: false }) : null;
    if (homeMk) homeMk.addTo(map);
    return () => { map.removeLayer(cluster); map.removeLayer(discLayer); if (homeMk) map.removeLayer(homeMk); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places, discounts, selectedId]);
  return null;
}

function UserMarker({ loc }) {
  const map = useMap();
  const ref = useRef({ marker: null, circle: null });
  useEffect(() => {
    if (!loc) return;
    if (!ref.current.marker) {
      ref.current.marker = L.marker([loc.lat, loc.lng], { icon: userDivIcon(), zIndexOffset: 3000, interactive: false }).addTo(map);
      ref.current.circle = L.circle([loc.lat, loc.lng], { radius: loc.accuracy || 40, color: "#2F7BF6", weight: 1, fillColor: "#2F7BF6", fillOpacity: 0.1 }).addTo(map);
    } else {
      ref.current.marker.setLatLng([loc.lat, loc.lng]);
      ref.current.circle.setLatLng([loc.lat, loc.lng]).setRadius(loc.accuracy || 40);
    }
  }, [loc, map]);
  useEffect(() => () => {
    if (ref.current.marker) map.removeLayer(ref.current.marker);
    if (ref.current.circle) map.removeLayer(ref.current.circle);
  }, [map]);
  return null;
}

function FitInitial({ places, discounts, home, userLoc }) {
  const map = useMap();
  const didBase = useRef(false);
  const didUser = useRef(false);
  const allPts = () => {
    const pts = places.map((p) => [p.lat, p.lng]);
    (discounts || []).forEach((d) => { if (isFinite(d.lat) && isFinite(d.lng)) pts.push([d.lat, d.lng]); });
    return pts;
  };
  useEffect(() => {
    if (didBase.current) return;
    const pts = allPts();
    if (home) pts.push([home.lat, home.lng]);
    if (!pts.length) return;
    try { map.fitBounds(pts, { padding: [60, 90], maxZoom: 16 }); } catch (_) {}
    didBase.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places, discounts, home]);
  useEffect(() => {
    if (didUser.current || !userLoc || !home) return;
    if (distanceKm(userLoc, home) > 30) { didUser.current = true; return; }
    const pts = allPts();
    pts.push([home.lat, home.lng], [userLoc.lat, userLoc.lng]);
    try { map.fitBounds(pts, { padding: [60, 90], maxZoom: 16 }); } catch (_) {}
    didUser.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoc]);
  return null;
}

export default function ConciergeMap({ places = [], discounts = [], home, homeColor, lang = "it", initialCat = "__all__", catLabel, discountCatStyle, discountCatLabel, discountsMode = false, title, onClose, onSelect, onSelectDiscount, selectedId }) {
  const [cat, setCat] = useState(initialCat || "__all__");
  const [userLoc, setUserLoc] = useState(null);
  const [geoError, setGeoError] = useState(false);
  const [showGpsNote, setShowGpsNote] = useState(true);
  const mapRef = useRef(null);

  const discWithCoords = useMemo(() => discounts.filter((d) => isFinite(d.lat) && isFinite(d.lng)), [discounts]);

  // In modalità sconti i chip derivano dalle categorie sconto; altrimenti dai posti.
  const cats = useMemo(() => {
    const src = discountsMode ? discWithCoords.map((d) => d.category) : places.map((p) => p.category);
    return ["__all__", ...Array.from(new Set(src.filter(Boolean)))];
  }, [discountsMode, places, discWithCoords]);

  const filteredPlaces = useMemo(() => {
    if (discountsMode) return [];
    return cat === "__all__" ? places : places.filter((p) => p.category === cat);
  }, [cat, places, discountsMode]);

  const filteredDiscounts = useMemo(() => {
    if (!discountsMode) return discWithCoords; // overlay sempre visibile sulla mappa consigli
    return cat === "__all__" ? discWithCoords : discWithCoords.filter((d) => d.category === cat);
  }, [cat, discWithCoords, discountsMode]);

  const chipStyleFn = discountsMode ? (discountCatStyle || (() => null)) : resolveCategoryStyle;
  const chipLabelFn = discountsMode ? (discountCatLabel || ((c) => c)) : catLabel;
  const totalCount = discountsMode ? discWithCoords.length : places.length;

  useEffect(() => {
    if (!("geolocation" in navigator)) { setGeoError(true); return; }
    const id = navigator.geolocation.watchPosition(
      (pos) => { setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }); setGeoError(false); setShowGpsNote(false); },
      () => { setGeoError(true); setShowGpsNote(false); },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    return () => { try { navigator.geolocation.clearWatch(id); } catch (_) {} };
  }, []);

  const onLocate = () => {
    if (userLoc && mapRef.current) { mapRef.current.setView([userLoc.lat, userLoc.lng], 16); return; }
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { const l = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }; setUserLoc(l); setGeoError(false); mapRef.current && mapRef.current.setView([l.lat, l.lng], 16); },
        () => setGeoError(true), { enableHighAccuracy: true }
      );
    }
  };

  const center = home && isFinite(home.lat) ? [home.lat, home.lng]
    : (places[0] ? [places[0].lat, places[0].lng] : (discWithCoords[0] ? [discWithCoords[0].lat, discWithCoords[0].lng] : [41.9, 12.5]));

  return (
    <div data-testid="wb3-concierge-map" style={{ position: "fixed", inset: 0, zIndex: 55, maxWidth: 430, margin: "0 auto", background: "var(--cream)" }}>
      {/* Mappa a tutto schermo (dietro barra e menu) */}
      <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
        <MapContainer center={center} zoom={14} zoomControl={false} attributionControl={false} style={{ width: "100%", height: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapController onReady={(m) => { mapRef.current = m; }} />
          <MarkersLayer places={filteredPlaces} discounts={filteredDiscounts} home={home && isFinite(home.lat) ? home : null} homeColor={homeColor} onSelect={onSelect} onSelectDiscount={onSelectDiscount} selectedId={selectedId} catLabel={catLabel} />
          <UserMarker loc={userLoc} />
          <FitInitial places={discountsMode ? [] : places} discounts={discWithCoords} home={home && isFinite(home.lat) ? home : null} userLoc={userLoc} />
        </MapContainer>
      </div>

      {/* Attribuzione OpenStreetMap (richiesta dal fornitore) */}
      <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" data-testid="wb3-map-attrib"
        style={{ position: "absolute", left: 8, bottom: "calc(16px + env(safe-area-inset-bottom))", zIndex: 3, fontSize: 10, color: "#4b4741", background: "rgba(255,255,255,.72)", padding: "2px 6px", borderRadius: 6, textDecoration: "none" }}>© OpenStreetMap</a>

      {/* Barra in alto */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 4, padding: "14px 14px 10px", background: "linear-gradient(180deg, var(--cream) 55%, rgba(251,247,240,0))" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={onClose} data-testid="wb3-concierge-map-close" aria-label={cmt("chiudi", lang)}
            style={{ width: 44, height: 44, borderRadius: 22, background: "#fff", border: "none", cursor: "pointer", boxShadow: "0 6px 16px -8px rgba(22,19,15,.5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <X size={22} color="var(--ink)" />
          </button>
          <h2 className="disp" style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: "-.02em", flexGrow: 1 }}>{title || cmt("mappa", lang)}</h2>
          <span data-testid="wb3-concierge-map-count" style={{ fontSize: 14, fontWeight: 700, color: "var(--muted)", flexShrink: 0 }}>{totalCount} {cmt(discountsMode ? "sconti" : "posti", lang)}</span>
        </div>
        {/* Filtri categoria */}
        <div className="scroll" style={{ display: "flex", gap: 8, overflowX: "auto", marginTop: 10, paddingBottom: 2 }}>
          {cats.map((c) => {
            const active = cat === c;
            const st = c === "__all__" ? null : chipStyleFn(c);
            const CatIcon = st ? st.Icon : null;
            return (
              <button key={c} onClick={() => setCat(c)} data-testid={`wb3-map-cat-${c}`}
                style={{ flex: "0 0 auto", height: 38, padding: c === "__all__" ? "0 18px" : "0 14px 0 6px", borderRadius: 19, fontSize: 14, fontWeight: 700, border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 7, background: active ? "var(--ink)" : "#fff", color: active ? "#fff" : "var(--ink)", boxShadow: active ? "none" : "0 5px 14px -10px rgba(22,19,15,.6)" }}>
                {st && <span style={{ width: 26, height: 26, borderRadius: 13, background: st.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><CatIcon size={15} color="#fff" strokeWidth={2} /></span>}
                {c === "__all__" ? cmt("tutti", lang) : (chipLabelFn ? chipLabelFn(c) : c)}
              </button>
            );
          })}
        </div>
        {showGpsNote && !geoError && (
          <div data-testid="wb3-map-gps-note" style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.35, color: "var(--ink)", background: "rgba(255,255,255,.9)", borderRadius: 12, padding: "8px 12px", boxShadow: "0 6px 16px -10px rgba(22,19,15,.5)" }}>{cmt("gpsSpiega", lang)}</div>
        )}
        {geoError && (
          <div data-testid="wb3-map-gps-error" style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.35, color: "#8a5200", background: "rgba(255,243,214,.95)", borderRadius: 12, padding: "8px 12px" }}>{cmt("gpsNo", lang)}</div>
        )}
      </div>

      {/* Controlli a destra */}
      <div style={{ position: "absolute", right: 14, bottom: "calc(24px + env(safe-area-inset-bottom))", zIndex: 4, display: "flex", flexDirection: "column", gap: 10 }}>
        <button onClick={() => mapRef.current && mapRef.current.zoomIn()} data-testid="wb3-map-zoom-in" aria-label="Zoom +"
          style={{ width: 50, height: 50, borderRadius: 25, background: "#fff", border: "none", cursor: "pointer", boxShadow: "0 8px 20px -10px rgba(22,19,15,.55)", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={22} color="var(--ink)" /></button>
        <button onClick={() => mapRef.current && mapRef.current.zoomOut()} data-testid="wb3-map-zoom-out" aria-label="Zoom −"
          style={{ width: 50, height: 50, borderRadius: 25, background: "#fff", border: "none", cursor: "pointer", boxShadow: "0 8px 20px -10px rgba(22,19,15,.55)", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={22} color="var(--ink)" /></button>
        <button onClick={onLocate} data-testid="wb3-map-locate" aria-label={cmt("centra", lang)}
          style={{ width: 50, height: 50, borderRadius: 25, background: "#fff", border: "none", cursor: "pointer", boxShadow: "0 8px 20px -10px rgba(22,19,15,.55)", display: "flex", alignItems: "center", justifyContent: "center" }}><LocateFixed size={22} color="#2F7BF6" /></button>
      </div>
    </div>
  );
}
